/**
 * Utility functions for exporting table data to CSV format
 */

/**
 * Escapes CSV cell content to handle special characters
 * @param {string} cell - The cell content to escape
 * @returns {string} - Escaped cell content
 */
const escapeCSVCell = (cell) => {
  if (cell == null) return '';

  const cellStr = String(cell);

  // Check if cell needs quotes (contains comma, quotes, or newlines)
  if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n') || cellStr.includes('\r')) {
    // Escape quotes by doubling them
    return `"${cellStr.replace(/"/g, '""')}"`;
  }

  return cellStr;
};

/**
 * Converts table data to CSV format
 * @param {Array} tableRowsResults - Array of table row data
 * @param {Array} docColumns - Array of document column data
 * @returns {string} - CSV formatted string
 */
export const convertTableToCSV = (tableRowsResults, docColumns) => {
  // Create header row
  const headers = ['Question'];

  // Add document filenames as column headers
  docColumns.forEach((docs) => {
    if (Array.isArray(docs)) {
      docs.forEach((doc) => {
        headers.push(doc.filename || 'Untitled');
      });
    }
  });

  // Create data rows
  const rows = [];

  tableRowsResults.forEach((row) => {
    // Get the question text
    const question = row.prompt?.display_label || row.prompt?.prompt || '';
    const rowData = [question];

    // Add cell answers
    if (row.cells && Array.isArray(row.cells)) {
      row.cells.forEach((cell) => {
        // Extract answer text from cell
        const answer = cell.answer || cell.result || '';
        rowData.push(answer);
      });
    }

    // Ensure row has same number of columns as headers
    while (rowData.length < headers.length) {
      rowData.push('');
    }

    rows.push(rowData);
  });

  // Convert to CSV format
  const csvRows = [headers.map(escapeCSVCell).join(','), ...rows.map((row) => row.map(escapeCSVCell).join(','))];

  return csvRows.join('\n');
};

/**
 * Downloads CSV content as a file
 * @param {string} csvContent - The CSV content to download
 * @param {string} filename - The filename for the download
 */
export const downloadCSV = (csvContent, filename = 'table-export.csv') => {
  try {
    // Create blob with CSV content
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

    // Create download link
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';

    // Trigger download
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading CSV:', error);
    throw new Error('Failed to download CSV file');
  }
};
